import { ContextFragment } from "@pathwright/web/src/modules/api/generated"
import { getNextEntries } from "@pathwright/web/src/modules/home/getStoredActiveEntries"
import {
  baseEntries,
  getHomeActivePath
} from "@pathwright/web/src/modules/home/useHomeActiveEntries"
import { canViewUnifiedHome } from "@pathwright/web/src/modules/unified-home/useCanViewUnifiedHome"
import { History } from "history"
import {
  authRedirect,
  belltowerHomeRedirect
} from "lib/core/routing/middleware"
import Homescreen from "./Homescreen"

type RouteParams = {
  section: "learn" | "teach" | "design" | "unified"
  subSection?: string
}

type RouteState = {
  params: RouteParams
  match: { params: RouteParams }
  pwContext: ContextFragment
  location: Location & { query: { id?: string } }
  history: History & {
    location: Location & {
      state?: {
        redirectedFrom?: Location
      }
    }
  }
}

async function getHomeActivePathRedirect(nextState: RouteState, replace: any) {
  const hasFeaturePerm = await canViewUnifiedHome()

  // When entering /home/unified/, we need to set the unified home entry as active.
  if (nextState.params.section === "unified") {
    const entries = getNextEntries(["paths.unified"], baseEntries)
    localStorage.setItem("homeActiveEntries", JSON.stringify(entries))
    // If entering /home/ and not via redirect, clear active entries (not applicable to new Home Screen).
    // Unfortunately, this does prematurely clear the active unified home entry (user could be navigating
    // within the app and returning to /home/), but for now this is acceptable, and likely necessary to
    // prevent users in production who can currently access new nav menus due to a bug.
  } else if (
    !nextState.params.section &&
    !nextState.history.location.state?.redirectedFrom
  ) {
    localStorage.removeItem("homeActiveEntries")
  }

  if (hasFeaturePerm) {
    // If user has perm and at /home/unified/ then redirect them to /home/
    // which is standard route.
    if (nextState.params.section) {
      return replace("/home/")
    }
    // Allowing user to view unified Home Screen at /home/unified/ without
    // School having feature enabled (convenience). If route is simply /home/
    // though, we require School to have feature enabled.
    // If no section is selected yet, select the user's active section.
  } else if (!nextState.params.section) {
    const homeActivePath = getHomeActivePath(nextState.pwContext)
    return replace(homeActivePath)
  }
}

// Return the first redirect we find (if any).
async function getHomeRedirect(nextState: RouteState, replace: any) {
  const redirs = await Promise.all([
    authRedirect(nextState, replace),
    belltowerHomeRedirect(nextState, replace),
    getHomeActivePathRedirect(nextState, replace)
  ])

  const redir = redirs.find(Boolean)
  return redir
}

export async function onEnterHome(
  nextState: RouteState,
  replace: any,
  callback: any
) {
  callback(() => getHomeRedirect(nextState, replace))
}

export default [
  {
    path: "/home/:section(unified)?/",
    onEnter: onEnterHome,
    component: Homescreen
  }
]
